export class AppError extends Error {
  title: string

  constructor({ title, message }: { title: string; message?: string }) {
    super()
    this.title = title
    if (message) this.message = message
  }
}

export type ResponseErrorData = {
  type: string
  message: string
}

// FastAPI/Pydantic validation errors arrive as `detail: [{ type, loc, msg, ... }]`.
// Collapse that (or a plain string detail) into a single human-readable string so
// `ApiRequestError.message` is always a string and never leaks an object into JSX.
const normalizeDetail = (detail: unknown): string => {
  if (typeof detail === "string") return detail
  if (Array.isArray(detail)) {
    return detail
      .map((item) =>
        item && typeof item === "object" && "msg" in item
          ? String((item as { msg: unknown }).msg)
          : String(item),
      )
      .filter(Boolean)
      .join(". ")
  }
  return ""
}

export class ApiRequestError extends Error {
  status: number
  type: string
  message: string
  body: Record<string, unknown>

  constructor(body: Record<string, unknown>, response: Response) {
    super()

    this.name = "ApiRequestError"
    this.status = response.status
    this.type = (body.type as string) ?? "ApiRequestError"
    this.message =
      (body.message as string) ||
      normalizeDetail(body.detail) ||
      "Unknown error"
    this.body = body
  }
}

export class ExpiredTokenError extends AppError {
  constructor(message?: string) {
    super({
      title: "Sesión expirada",
    })

    this.message =
      message ??
      "La sesión para firmar el documento ha expirado. Para continuar debe acceder al link disponible en su correo electrónico"

    this.name = "ExpiredTokenError"
  }
}

export class InvalidAuthorizationCodeError extends AppError {
  resendToken?: string
  resendTokenExpiresAt?: string
  signeeEmailHint?: string

  constructor(opts?: {
    message?: string
    resendToken?: string
    resendTokenExpiresAt?: string
    signeeEmailHint?: string
  }) {
    super({
      title: "Invitación inválida",
    })

    this.message =
      opts?.message ??
      "Este enlace ya no es válido o ha expirado. Si recibiste un correo más reciente, ingresa desde ese enlace. Si no, solicita una nueva invitación o contacta a soporte."

    this.resendToken = opts?.resendToken
    this.resendTokenExpiresAt = opts?.resendTokenExpiresAt
    this.signeeEmailHint = opts?.signeeEmailHint

    this.name = "InvalidAuthorizationCodeError"
  }
}

export class InvalidResendTokenError extends AppError {
  constructor(message?: string) {
    super({
      title: "Solicitud expirada",
    })

    this.message =
      message ??
      "Este enlace ya no es válido. Refresca la página para intentar de nuevo."

    this.name = "InvalidResendTokenError"
  }
}

export class InvitationNotActionableError extends AppError {
  constructor(message?: string) {
    super({
      title: "Documento ya procesado",
    })

    this.message = message ?? "Este documento ya fue procesado."

    this.name = "InvitationNotActionableError"
  }
}

export class InvitationNotFoundError extends AppError {
  constructor(message?: string) {
    super({
      title: "Invitación no encontrada",
    })

    this.message =
      message ??
      "No encontramos esta invitación o ya ha expirado. Solicita nuevamente el correo con el enlace de invitación."

    this.name = "InvitationNotFoundError"
  }
}
